import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Stack,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { churnApi } from '../../services/api/churnApi';

const REASON_LABELS: Record<string, string> = {
  // 탈퇴
  not_useful: '앱이 유용하지 않아요',
  privacy: '개인정보가 걱정돼요',
  too_many_notifications: '알림이 너무 많아요',
  found_alternative: '다른 앱을 사용해요',
  temporary: '잠시 쉬고 싶어요',
  other: '기타',
  // 해지
  expensive: '가격이 부담돼요',
  not_using: '자주 사용하지 않아요',
};

const COLORS = ['#2A7789', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#607D8B'];

const ChurnAnalysisPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [listType, setListType] = useState<'account_deletion' | 'subscription_cancel'>('account_deletion');
  const [page, setPage] = useState(1);

  const statsQuery = useQuery({
    queryKey: ['churn-stats', startDate, endDate],
    queryFn: () => churnApi.getStats({ startDate: startDate || undefined, endDate: endDate || undefined }),
  });

  const listQuery = useQuery({
    queryKey: ['churn-list', listType, page, startDate, endDate],
    queryFn: () =>
      churnApi.getList({ type: listType, page, limit: 20, startDate: startDate || undefined, endDate: endDate || undefined }),
  });

  const stats = statsQuery.data?.data.data;
  const listData = listQuery.data?.data.data;

  const deletionChartData = (stats?.deletion || []).map(d => ({
    name: REASON_LABELS[d.reason] || d.reason,
    value: parseInt(d.count),
  }));

  const cancelChartData = (stats?.cancel || []).map(d => ({
    name: REASON_LABELS[d.reason] || d.reason,
    value: parseInt(d.count),
  }));

  const monthlyData = (stats?.monthlyTrend || []).reduce<Record<string, any>>((acc, item) => {
    const month = item.month?.slice(0, 7) || '';
    if (!acc[month]) acc[month] = { month };
    acc[month][item.type] = parseInt(item.count);
    return acc;
  }, {});

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight="bold" mb={3}>이탈 분석</Typography>

      {/* 날짜 필터 */}
      <Stack direction="row" spacing={2} mb={3}>
        <TextField
          label="시작일"
          type="date"
          size="small"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="종료일"
          type="date"
          size="small"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="통계 차트" />
        <Tab label="상세 목록" />
      </Tabs>

      {tab === 0 && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            {/* 탈퇴 이유 파이차트 */}
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>회원 탈퇴 이유</Typography>
                {deletionChartData.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>데이터 없음</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={deletionChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                        {deletionChartData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* 구독 해지 이유 파이차트 */}
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>구독 해지 이유</Typography>
                {cancelChartData.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" py={4}>데이터 없음</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={cancelChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                        {cancelChartData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* 월별 추이 */}
          <Box mt={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>월별 이탈 추이</Typography>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={Object.values(monthlyData)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend formatter={(v) => v === 'account_deletion' ? '회원 탈퇴' : '구독 해지'} />
                    <Bar dataKey="account_deletion" fill="#F44336" name="account_deletion" />
                    <Bar dataKey="subscription_cancel" fill="#FF9800" name="subscription_cancel" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Box>
        </>
      )}

      {tab === 1 && (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1} mb={2}>
              <Chip
                label="회원 탈퇴"
                color={listType === 'account_deletion' ? 'primary' : 'default'}
                onClick={() => { setListType('account_deletion'); setPage(1); }}
              />
              <Chip
                label="구독 해지"
                color={listType === 'subscription_cancel' ? 'primary' : 'default'}
                onClick={() => { setListType('subscription_cancel'); setPage(1); }}
              />
            </Stack>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>이유</TableCell>
                    <TableCell>상세</TableCell>
                    <TableCell>이메일</TableCell>
                    <TableCell>구독 플랜</TableCell>
                    <TableCell>일시</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(listData?.items || []).map(item => (
                    <TableRow key={item.id}>
                      <TableCell>{REASON_LABELS[item.reason] || item.reason}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.detail || '-'}
                      </TableCell>
                      <TableCell>{item.userEmail || '-'}</TableCell>
                      <TableCell>{item.subscriptionPlan || '-'}</TableCell>
                      <TableCell>{new Date(item.createdAt).toLocaleDateString('ko-KR')}</TableCell>
                    </TableRow>
                  ))}
                  {(listData?.items || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        데이터가 없습니다
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {listData && listData.totalPages > 1 && (
              <Stack direction="row" justifyContent="center" spacing={1} mt={2}>
                {Array.from({ length: listData.totalPages }, (_, i) => (
                  <Chip
                    key={i + 1}
                    label={i + 1}
                    size="small"
                    color={page === i + 1 ? 'primary' : 'default'}
                    onClick={() => setPage(i + 1)}
                  />
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ChurnAnalysisPage;
